import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import store from 'store/store';
import { ICourseConfigItems } from 'store/reducers/courseConfig';
import { IRefIds } from 'constants/refs';
import { IConceptLinkerAiLinkerItem, IConceptLinkerAiLinkerDialectResult } from 'types/conceptLinkerAiLinker';
import { ILinkedConcept } from 'types/conceptLinker';
import { getTooltip } from 'components/conceptLink/tooltips';
import { AiLinkerPhraseEngine, IAiLinkerPhraseData } from 'components/conceptLink/AiLinkerPhraseEngine';
import InflectStudied from 'components/conceptLink/components/InflectStudied';
import DeltaPos from 'components/conceptLink/components/DeltaPos';
import GuessedVoc from 'components/conceptLink/GuessedVoc';
import GuessedPanel from 'components/conceptLink/GuessedPanel';
import GuessedCompound from 'components/conceptLink/GuessedCompound';
import GuessedCulturals from 'components/conceptLink/GuessedCulturals';
import ModuleContext from 'CMFW/context/ModuleContext';
import ConceptLinkerAiLinkerItemsModel from 'models/ConceptLinkerAiLinkerItemsModel';

const TOOLTIP_DELAY = 250;
const TOOLTIP_WIDTH = 300;

// ── Engine registry (persists across renders) ──────────────────────
const engineRegistry = new Map<string, AiLinkerPhraseEngine>();

function getEngineKey(itemId: number, dialectId: string, phraseOrder: number): string {
  return `${itemId}-${dialectId}-${phraseOrder}`;
}

export function clearEngineRegistry() {
  engineRegistry.clear();
}

// ── Global tooltip ──────────────────────────────────────────────────
let tooltipEl: HTMLDivElement | null = null;
let tooltipTimer: ReturnType<typeof setTimeout> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let listenerInstalled = false;
let highlightedEls: HTMLElement[] = [];
let currentItemsModel: ConceptLinkerAiLinkerItemsModel | null = null;

const HIGHLIGHT_CLASS = 'ailinker-word-highlight';

// Inject CSS once
(() => {
  if (typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = `
    .${HIGHLIGHT_CLASS} { background-color: #ffff58 !important; }
    .ailinker-word-none { border-bottom: double 3px red; }
    .ailinker-word-pending { border-bottom: solid 3px #ffb611; cursor: help; }
    .ailinker-word-done { border-bottom: solid 1px #9dd69b; cursor: help; }
    .ailinker-word-ai { background-color: #fffde7; border-bottom: solid 1px #9dd69b; cursor: help; border-radius: 2px; padding: 1px 2px; }
    .ailinker-word-mismatch { box-shadow: 0 0 0 2px #ce93d8; }
    .ailinker-dialect-label { display: inline-block; font-size: 11px; font-weight: 700; color: #7b1fa2; background: #f3e5f5; border: 1px solid #ce93d8; border-radius: 3px; padding: 0 4px; margin-right: 6px; vertical-align: middle; line-height: 1.6; }
    .ailinker-dialect-separator { border-top: 1px solid #e0e0e0; margin: 4px 0; }
  `;
  document.head.appendChild(style);
})();

function getDialectCode(courseId: number, dialectId: string): string {
  const courseConfig = (store.getState().courseConfig as ICourseConfigItems)[courseId];
  if (!courseConfig) return dialectId;
  const dialect = courseConfig.targetDialects.find((d) => d.dialectId === parseInt(dialectId, 10));
  return dialect?.dialectCode || dialectId;
}

function clearHighlights() {
  highlightedEls.forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
  highlightedEls = [];
}

function addHighlightToPositions(target: HTMLElement, positions: number[]) {
  clearHighlights();
  const row = target.closest('.ag-row');
  if (!row) return;
  const phraseOrder = target.getAttribute('data-ailinker-phrase');
  const dialectId = target.getAttribute('data-ailinker-dialect');
  if (!phraseOrder || !dialectId) return;

  positions.forEach((pos) => {
    const els = row.querySelectorAll<HTMLElement>(
      `[data-ailinker-dialect="${dialectId}"][data-ailinker-phrase="${phraseOrder}"][data-ailinker-pos="${pos}"]`,
    );
    els.forEach((el) => {
      el.classList.add(HIGHLIGHT_CLASS);
      highlightedEls.push(el);
    });
  });
}

function getTooltipEl(): HTMLDivElement {
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'ai-linker-tooltip-root';
    document.body.appendChild(tooltipEl);

    tooltipEl.addEventListener('mouseenter', () => cancelHideTooltip());
    tooltipEl.addEventListener('mouseleave', () => scheduleHideTooltip());

    document.addEventListener('mousedown', (e) => {
      if (tooltipEl && !tooltipEl.contains(e.target as Node)) {
        hideTooltip();
      }
    });

    window.addEventListener('scroll', () => hideTooltip(), true);
  }
  return tooltipEl;
}

function hideTooltip() {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  clearHighlights();
  const el = getTooltipEl();
  ReactDOM.unmountComponentAtNode(el);
}

function scheduleHideTooltip() {
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    hideTooltip();
  }, 200);
}

function cancelHideTooltip() {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function showLinkedTooltip(
  concept: ILinkedConcept,
  engine: AiLinkerPhraseEngine,
  rect: DOMRect,
  target: HTMLElement,
) {
  const el = getTooltipEl();
  const courseId = concept.courseId ?? engine.getCourse();
  const targetDialectId = engine.getTargetDialectId();

  let x = rect.left;
  if (x + TOOLTIP_WIDTH > window.innerWidth) {
    x = window.innerWidth - TOOLTIP_WIDTH - 10;
  }
  let y = rect.bottom + 2;
  if (y + 190 > window.innerHeight) {
    y = rect.top - 192;
  }

  addHighlightToPositions(target, concept.positions);

  const handleInvalidate = () => {
    engine.invalidateConcept(concept);
    updateWordClasses(engine);
    updateMismatchClasses(target);
    persistEngineChanges(engine);
    hideTooltip();
  };

  ReactDOM.render(
    <Provider store={store}>
      <TooltipContainer $x={x} $y={y} onClick={() => hideTooltip()}>
        <TooltipHeader>
          <InvalidateButton onClick={(e) => { e.stopPropagation(); handleInvalidate(); }} icon={faRedo} />
          {concept.courseId != null && <CourseTag>C{concept.courseId}</CourseTag>}
          <InflectStudied studied={concept.inflectStudied} />
          <DeltaPos deltaPos={concept.deltaPos} />
        </TooltipHeader>
        {getTooltip({ ref: concept.ref, refId: concept.refId, courseId, targetDialectId })}
      </TooltipContainer>
    </Provider>,
    el,
  );
}

const refLabels: Record<number, string> = {
  [IRefIds.vocs]: 'V',
  [IRefIds.vocsCourse]: 'V',
  [IRefIds.panels]: 'P',
  [IRefIds.compounds]: 'C',
  [IRefIds.cultural]: 'CV',
};

function renderGuessedContent(concept: ILinkedConcept, fallbackCourseId: number, targetDialectId: number) {
  const courseId = concept.courseId ?? fallbackCourseId;
  switch (concept.ref) {
    case IRefIds.vocs:
    case IRefIds.vocsCourse:
      return <GuessedVoc id={concept.refId} courseId={courseId} targetDialectId={targetDialectId} />;
    case IRefIds.panels:
      return <GuessedPanel id={concept.refId} courseId={courseId} targetDialectId={targetDialectId} />;
    case IRefIds.compounds:
      return <GuessedCompound id={concept.refId} courseId={courseId} targetDialectId={targetDialectId} />;
    case IRefIds.cultural:
      return <GuessedCulturals id={concept.refId} courseId={courseId} targetDialectId={targetDialectId} />;
    default:
      return null;
  }
}

function showGuessedTooltip(
  concepts: ILinkedConcept[],
  engine: AiLinkerPhraseEngine,
  rect: DOMRect,
  target: HTMLElement,
) {
  const el = getTooltipEl();
  const courseId = engine.getCourse();
  const targetDialectId = engine.getTargetDialectId();

  let x = rect.left;
  if (x + TOOLTIP_WIDTH > window.innerWidth) {
    x = window.innerWidth - TOOLTIP_WIDTH - 10;
  }
  let y = rect.bottom + 2;
  if (y + 190 > window.innerHeight) {
    y = rect.top - 192;
  }

  // Group by unique position sets
  const uniquePositionSets: Array<{ positions: number[]; concepts: ILinkedConcept[] }> = [];
  concepts.forEach((c) => {
    const key = JSON.stringify(c.positions.slice().sort());
    const existing = uniquePositionSets.find((u) => JSON.stringify(u.positions.slice().sort()) === key);
    if (existing) {
      existing.concepts.push(c);
    } else {
      uniquePositionSets.push({ positions: c.positions, concepts: [c] });
    }
  });

  const handleSelect = (concept: ILinkedConcept) => {
    engine.selectConcept(concept);
    updateWordClasses(engine);
    updateMismatchClasses(target);
    persistEngineChanges(engine);
    hideTooltip();
  };

  // If only one position set, show concepts directly
  const conceptsToShow = uniquePositionSets.length === 1 ? uniquePositionSets[0].concepts : concepts;

  ReactDOM.render(
    <Provider store={store}>
      <TooltipContainer $x={x} $y={y}>
        {conceptsToShow.map((concept, i) => (
          <GuessedConceptRow
            key={`${concept.ref}-${concept.refId}-${i}`}
            onClick={() => handleSelect(concept)}
            onMouseEnter={() => addHighlightToPositions(target, concept.positions)}
          >
            <TypeBadge>{refLabels[concept.ref] || '?'}</TypeBadge>
            {concept.courseId != null && <CourseTag>C{concept.courseId}</CourseTag>}
            <InflectStudied studied={concept.inflectStudied} />
            <DeltaPos deltaPos={concept.deltaPos} />
            <GuessedTitles>
              {renderGuessedContent(concept, courseId, targetDialectId)}
            </GuessedTitles>
          </GuessedConceptRow>
        ))}
      </TooltipContainer>
    </Provider>,
    el,
  );
}

/** Update DOM classes for all word spans belonging to this engine */
function updateWordClasses(engine: AiLinkerPhraseEngine) {
  // Find the engine key
  let engineKey: string | null = null;
  for (const [key, eng] of engineRegistry.entries()) {
    if (eng === engine) {
      engineKey = key;
      break;
    }
  }
  if (!engineKey) return;

  const linkedPositions = engine.getLinkedPositions();
  const allConceptPositions = engine.getAllConceptPositions();

  const wordEls = document.querySelectorAll<HTMLElement>(`[data-ailinker-engine="${engineKey}"]`);
  wordEls.forEach((el) => {
    const pos = parseInt(el.getAttribute('data-ailinker-pos') || '', 10);
    if (isNaN(pos)) return;

    const isLinked = linkedPositions.indexOf(pos) !== -1;
    const isPending = !isLinked && allConceptPositions.indexOf(pos) !== -1;

    // Remove all state classes
    el.classList.remove('ailinker-word-none', 'ailinker-word-pending', 'ailinker-word-done', 'ailinker-word-ai');

    if (isLinked) {
      // Check if this was originally AI-selected (keep yellow bg)
      const isAi = el.getAttribute('data-ailinker-ai') === '1';
      el.classList.add(isAi ? 'ailinker-word-ai' : 'ailinker-word-done');
    } else if (isPending) {
      el.classList.add('ailinker-word-pending');
    } else {
      el.classList.add('ailinker-word-none');
    }
  });
}

/** Update mismatch classes across all dialects for this item */
function updateMismatchClasses(target: HTMLElement) {
  const row = target.closest('.ag-row');
  if (!row) return;

  const itemAttr = target.getAttribute('data-ailinker-item');
  if (!itemAttr) return;

  recalcMismatchesForItem(row as HTMLElement, itemAttr);
}

/** Recalculate mismatch highlighting for all dialects of an item within a row */
function recalcMismatchesForItem(row: HTMLElement, itemId: string) {
  // Get all dialect IDs for this item
  const dialectEls = row.querySelectorAll<HTMLElement>(`[data-ailinker-item="${itemId}"][data-ailinker-dialect]`);
  const dialectIds = new Set<string>();
  dialectEls.forEach((el) => {
    const did = el.getAttribute('data-ailinker-dialect');
    if (did) dialectIds.add(did);
  });

  if (dialectIds.size < 2) return;

  // For each phrase order, compare concepts across dialects
  const phraseOrders = new Set<string>();
  dialectEls.forEach((el) => {
    const po = el.getAttribute('data-ailinker-phrase');
    if (po) phraseOrders.add(po);
  });

  // Clear all existing mismatch classes for this item
  row.querySelectorAll<HTMLElement>(`[data-ailinker-item="${itemId}"].ailinker-word-mismatch`)
    .forEach((el) => el.classList.remove('ailinker-word-mismatch'));

  const dialectArr = Array.from(dialectIds);

  phraseOrders.forEach((phraseOrder) => {
    // Build position → concept map per dialect
    const dialectConceptMaps: Record<string, Record<number, string>> = {};

    dialectArr.forEach((dialectId) => {
      const engineKey = `${itemId}-${dialectId}-${phraseOrder}`;
      const engine = engineRegistry.get(engineKey);
      if (!engine) return;

      const conceptMap: Record<number, string> = {};
      engine.linkedConcepts.forEach((c) => {
        const conceptKey = `${c.ref}:${c.refId}`;
        c.positions.forEach((pos) => {
          conceptMap[pos] = conceptKey;
        });
      });
      dialectConceptMaps[dialectId] = conceptMap;
    });

    // Get all positions across all dialects
    const allPositions = new Set<number>();
    Object.values(dialectConceptMaps).forEach((map) => {
      Object.keys(map).forEach((pos) => allPositions.add(parseInt(pos, 10)));
    });

    // For each position check if concept is same across all dialects that have it
    allPositions.forEach((pos) => {
      const conceptsAtPos = new Set<string>();
      const hasConceptIn = new Set<string>();

      dialectArr.forEach((dialectId) => {
        const map = dialectConceptMaps[dialectId];
        if (map && map[pos] !== undefined) {
          conceptsAtPos.add(map[pos]);
          hasConceptIn.add(dialectId);
        }
      });

      // Mismatch if: different concepts, OR one dialect has concept and another doesn't
      const hasMismatch = conceptsAtPos.size > 1 ||
        (conceptsAtPos.size === 1 && hasConceptIn.size < dialectArr.length);

      if (hasMismatch) {
        dialectArr.forEach((dialectId) => {
          const els = row.querySelectorAll<HTMLElement>(
            `[data-ailinker-item="${itemId}"][data-ailinker-dialect="${dialectId}"][data-ailinker-phrase="${phraseOrder}"][data-ailinker-pos="${pos}"]`,
          );
          els.forEach((el) => el.classList.add('ailinker-word-mismatch'));
        });
      }
    });
  });
}

function persistEngineChanges(engine: AiLinkerPhraseEngine) {
  if (!currentItemsModel) return;

  // Find the item ID and dialect from the engine registry
  for (const [key, eng] of engineRegistry.entries()) {
    if (eng === engine) {
      const parts = key.split('-');
      const itemId = parseInt(parts[0], 10);

      // Collect all phrase data per dialect for this item
      const dialects: Record<string, { phrases: IAiLinkerPhraseData[] }> = {};
      for (const [k, e] of engineRegistry.entries()) {
        if (k.startsWith(`${itemId}-`)) {
          const kParts = k.split('-');
          const dialectId = kParts[1];
          if (!dialects[dialectId]) {
            dialects[dialectId] = { phrases: [] };
          }
          dialects[dialectId].phrases.push(e.toPhrasesData());
        }
      }
      // Sort phrases within each dialect
      Object.values(dialects).forEach((d) => d.phrases.sort((a, b) => a.order - b.order));

      currentItemsModel.updateItemConcepts(itemId, dialects);
      break;
    }
  }
}

function installGlobalListener() {
  if (listenerInstalled) return;
  listenerInstalled = true;

  document.addEventListener(
    'mouseover',
    (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.('[data-ailinker-word]') as HTMLElement | null;
      if (!target) return;

      const engineKey = target.getAttribute('data-ailinker-engine');
      if (!engineKey) return;

      const engine = engineRegistry.get(engineKey);
      if (!engine) return;

      const position = parseInt(target.getAttribute('data-ailinker-pos') || '', 10);
      if (isNaN(position)) return;

      if (tooltipTimer) clearTimeout(tooltipTimer);
      cancelHideTooltip();
      hideTooltip();

      tooltipTimer = setTimeout(() => {
        const rect = target.getBoundingClientRect();
        const linkedConcept = engine.getLinkedConceptByPosition(position);

        if (linkedConcept) {
          showLinkedTooltip(linkedConcept, engine, rect, target);
        } else {
          const guessedConcepts = engine.getGuessedConceptsByPosition(position);
          if (guessedConcepts.length > 0) {
            showGuessedTooltip(guessedConcepts, engine, rect, target);
          }
        }
      }, TOOLTIP_DELAY);
    },
    true,
  );

  document.addEventListener(
    'mouseout',
    (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.('[data-ailinker-word]');
      if (!target) return;
      if (tooltipTimer) {
        clearTimeout(tooltipTimer);
        tooltipTimer = null;
      }
      // Give time to reach the tooltip before hiding
      scheduleHideTooltip();
    },
    true,
  );
}

// ── React cell renderer ──────────────────────────────────────────────

const AiLinkerTextRenderer: React.FC<ICellRendererParams> = (params) => {
  const data = params.data as IConceptLinkerAiLinkerItem;
  const itemsModel = React.useContext(ModuleContext) as ConceptLinkerAiLinkerItemsModel;
  const containerRef = useRef<HTMLDivElement>(null);

  // Track model reference for persistence
  currentItemsModel = itemsModel;

  useEffect(() => {
    installGlobalListener();
  }, []);

  const handleEngineUpdate = useCallback(
    (engine: AiLinkerPhraseEngine) => {
      updateWordClasses(engine);
      // Recalculate mismatch after engine update
      if (containerRef.current && data) {
        const row = containerRef.current.closest('.ag-row');
        if (row) {
          recalcMismatchesForItem(row as HTMLElement, String(data.id));
        }
      }
    },
    [data],
  );

  if (!data) {
    return null;
  }

  // Multi-dialect: ai_result.dialects
  const dialects = data.ai_result?.dialects;
  if (!dialects || Object.keys(dialects).length === 0) {
    return null;
  }

  const dialectEntries = Object.entries(dialects);
  const courseId = data.course_id;

  return (
    <TextContainer ref={containerRef}>
      {dialectEntries.map(([dialectId, dialectResult], dIdx) => (
        <React.Fragment key={dialectId}>
          {dIdx > 0 && <div className="ailinker-dialect-separator" />}
          <DialectBlock>
            <span className="ailinker-dialect-label">{getDialectCode(courseId, dialectId)}</span>
            {renderDialectPhrases(data, dialectId, dialectResult, courseId, handleEngineUpdate)}
          </DialectBlock>
        </React.Fragment>
      ))}
    </TextContainer>
  );
};

function renderDialectPhrases(
  data: IConceptLinkerAiLinkerItem,
  dialectId: string,
  dialectResult: IConceptLinkerAiLinkerDialectResult,
  courseId: number,
  handleEngineUpdate: (engine: AiLinkerPhraseEngine) => void,
) {
  if (!dialectResult.phrases || dialectResult.phrases.length === 0) {
    return dialectResult.text ? <span>{dialectResult.text}</span> : null;
  }

  return dialectResult.phrases.map((phrase, phraseIdx) => {
    const engineKey = getEngineKey(data.id, dialectId, phrase.order);

    // Get or create engine for this dialect+phrase
    let engine = engineRegistry.get(engineKey);
    if (!engine) {
      engine = new AiLinkerPhraseEngine({
        courseId,
        targetDialectId: dialectResult.dialect_id,
        entityRef: data.entity_ref,
        entityRefId: data.entity_ref_id,
        phraseData: phrase,
        onUpdate: handleEngineUpdate,
      });
      engineRegistry.set(engineKey, engine);

      // Trigger initial guess for unlinked positions
      const linkedPositions = engine.getLinkedPositions();
      if (linkedPositions.length < engine.words.length) {
        engine.guess(false);
      }
    }

    const linkedPositions = engine.getLinkedPositions();
    const allConceptPositions = engine.getAllConceptPositions();

    return (
      <PhraseSpan key={phraseIdx}>
        {phraseIdx > 0 && ' '}
        {engine.words.map((word, wordIdx) => {
          const isLinked = linkedPositions.indexOf(wordIdx) !== -1;
          const isPending = !isLinked && allConceptPositions.indexOf(wordIdx) !== -1;

          // Check if this linked concept was AI-selected
          let isAiSelected = false;
          if (isLinked) {
            const concept = phrase.concepts?.find(
              (c) => c.aiSelected && c.positions.indexOf(wordIdx) !== -1,
            );
            isAiSelected = !!concept;
          }

          let className = 'ailinker-word-none';
          if (isLinked && isAiSelected) {
            className = 'ailinker-word-ai';
          } else if (isLinked) {
            className = 'ailinker-word-done';
          } else if (isPending) {
            className = 'ailinker-word-pending';
          }

          return (
            <span
              key={wordIdx}
              className={className}
              data-ailinker-word="1"
              data-ailinker-engine={engineKey}
              data-ailinker-item={String(data.id)}
              data-ailinker-dialect={dialectId}
              data-ailinker-phrase={String(phrase.order)}
              data-ailinker-pos={String(wordIdx)}
              {...(isAiSelected ? { 'data-ailinker-ai': '1' } : {})}
            >
              {wordIdx > 0 && ' '}
              {word}
            </span>
          );
        })}
      </PhraseSpan>
    );
  });
}

const TextContainer = styled.div`
  white-space: normal;
  line-height: 1.8;
  font-size: 16px;
`;

const DialectBlock = styled.div`
  /* Normal inline flow (not flex): a flex parent blockifies each PhraseSpan
     into a flex item and strips its leading whitespace — the space between
     sentences — jamming adjacent phrases together (e.g. "Merhaba!Ev"). */
`;

const PhraseSpan = styled.span``;

const TooltipContainer = styled.div<{ $x: number; $y: number }>`
  position: fixed;
  top: ${(props) => props.$y}px;
  left: ${(props) => props.$x}px;
  width: ${TOOLTIP_WIDTH}px;
  border-radius: 2px;
  min-height: 90px;
  max-height: 190px;
  border: solid 1px #b9b9bb;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.25);
  overflow: auto;
  background-color: white;
  z-index: 9999;
  cursor: default;
`;

const TooltipHeader = styled.div`
  height: 20px;
  border-bottom: solid 1px darkgrey;
  background-color: #efefef;
  padding-left: 2px;
`;

const InvalidateButton = styled(FontAwesomeIcon)`
  color: #616161;
  text-align: right;
  border-radius: 2px;
  display: inline-block;
  font-size: 12px;
  margin-top: 2px;
  padding: 2px;
  cursor: pointer;
  margin-left: 4px;
`;

const GuessedConceptRow = styled.div`
  cursor: pointer;
  padding: 2px 4px;
  min-height: 22px;
  display: flex;
  flex-direction: row;
  align-items: center;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const TypeBadge = styled.div`
  font-weight: bold;
  color: darkgray;
  font-size: 14px;
  margin: 0;
  border: solid 1px gray;
  border-radius: 4px;
  padding: 1px 3px;
  height: fit-content;
`;

const CourseTag = styled.div`
  color: #1565c0;
  font-size: 14px;
  margin: 0;
  border: solid 1px #90caf9;
  border-radius: 4px;
  padding: 1px 3px;
  height: fit-content;
  background: #e3f2fd;
`;

const GuessedTitles = styled.div`
  display: block;
  flex: 1;
  background-color: #e2e1e1;
  padding: 1px 6px;
  border-radius: 4px;
  color: #403e3e;
  border: solid 1px #c7c7c7;

  & div:not(:last-child) {
    border-bottom: dashed 1px gray;
  }
`;

export default AiLinkerTextRenderer;
